import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { BookMarked, BookHeart, Users, Mail } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import GoogleAuthButton from "@/components/GoogleAuthButton";

export default function Register() {
    const { t } = useTranslation();
    const { register } = useAuth();
    const navigate = useNavigate();
    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [postalCode, setPostalCode] = useState("");
    const [country, setCountry] = useState("ES");
    const [emailOptin, setEmailOptin] = useState(true);
    const [loading, setLoading] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (password.length < 6) {
            toast.error(t("auth.toast_password_short"));
            return;
        }
        setLoading(true);
        const res = await register(
            email.trim().toLowerCase(),
            password,
            name.trim(),
            {
                email_recs_optin: emailOptin,
                postal_code: postalCode.trim() || null,
                country: country.trim().toUpperCase() || null,
            },
        );
        setLoading(false);
        if (res.ok) {
            toast.success(t("auth.toast_account_created"));
            navigate("/");
        } else {
            toast.error(res.error);
        }
    };

    const valueProps = [
        { icon: BookHeart, key: "value_save_recs" },
        { icon: Users, key: "value_join_clubs" },
        { icon: Mail, key: "value_monthly_email" },
    ];

    return (
        <main className="min-h-[calc(100vh-73px)] flex items-center justify-center px-5 py-12 md:py-16 bg-[var(--bg)]">
            <section className="w-full max-w-md">
                <Link
                    to="/"
                    data-testid="register-brand-link"
                    className="flex items-center justify-center gap-2 text-[var(--ink)] mb-8"
                >
                    <BookMarked size={18} strokeWidth={1.5} />
                    <span
                        className="font-display text-2xl tracking-tight"
                        style={{ fontVariationSettings: '"wght" 600, "wdth" 90, "opsz" 32' }}
                    >
                        {t("nav.brand")}
                    </span>
                </Link>

                <p className="text-xs uppercase tracking-[0.3em] text-[var(--accent)] mb-3 text-center">
                    {t("auth.register_eyebrow")}
                </p>
                <h1
                    className="font-display text-4xl sm:text-5xl text-[var(--ink)] text-center mb-3 tracking-tight"
                    style={{ fontVariationSettings: '"wght" 600, "wdth" 85, "opsz" 96' }}
                >
                    {t("auth.register_title")}
                </h1>
                <p className="text-center text-sm text-[var(--ink-muted)] mb-8 max-w-xs mx-auto">
                    {t("auth.register_brand_subline")}
                </p>

                {/* Value props */}
                <ul data-testid="register-value-props" className="space-y-3 mb-8">
                    {valueProps.map(({ icon: Icon, key }) => (
                        <li key={key} className="flex items-start gap-3 text-sm">
                            <Icon
                                size={16}
                                strokeWidth={1.5}
                                className="text-[var(--accent)] mt-0.5 flex-shrink-0"
                            />
                            <span className="text-[var(--ink-muted)] leading-relaxed">
                                {t(`auth.${key}`)}
                            </span>
                        </li>
                    ))}
                </ul>

                <GoogleAuthButton label={t("auth.google_register")} />
                <div className="flex items-center gap-4 my-8">
                    <div className="h-px flex-1 bg-[var(--border)]" />
                    <span className="text-xs uppercase tracking-widest text-[var(--ink-dim)]">
                        {t("auth.or_email")}
                    </span>
                    <div className="h-px flex-1 bg-[var(--border)]" />
                </div>

                <form onSubmit={handleSubmit} className="space-y-6">
                    <div>
                        <label className="block text-xs uppercase tracking-widest text-[var(--ink-muted)] mb-2">
                            {t("auth.name_optional")}
                        </label>
                        <input
                            data-testid="register-name-input"
                            type="text"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            className="w-full bg-transparent border-b border-[var(--ink)] py-2 text-lg text-[var(--ink)] focus:outline-none focus:border-[var(--accent)] transition-colors"
                        />
                    </div>
                    <div>
                        <label className="block text-xs uppercase tracking-widest text-[var(--ink-muted)] mb-2">
                            {t("auth.email")}
                        </label>
                        <input
                            data-testid="register-email-input"
                            type="email"
                            required
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            className="w-full bg-transparent border-b border-[var(--ink)] py-2 text-lg text-[var(--ink)] focus:outline-none focus:border-[var(--accent)] transition-colors"
                        />
                    </div>
                    <div>
                        <label className="block text-xs uppercase tracking-widest text-[var(--ink-muted)] mb-2">
                            {t("auth.password")}
                        </label>
                        <input
                            data-testid="register-password-input"
                            type="password"
                            required
                            minLength={6}
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            className="w-full bg-transparent border-b border-[var(--ink)] py-2 text-lg text-[var(--ink)] focus:outline-none focus:border-[var(--accent)] transition-colors"
                        />
                        <p className="text-xs text-[var(--ink-dim)] mt-1.5">
                            {t("auth.password_hint")}
                        </p>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-xs uppercase tracking-widest text-[var(--ink-muted)] mb-2">
                                {t("auth.postal_code_optional")}
                            </label>
                            <input
                                data-testid="register-postal-input"
                                type="text"
                                maxLength={20}
                                value={postalCode}
                                onChange={(e) => setPostalCode(e.target.value)}
                                placeholder="28013"
                                className="w-full bg-transparent border-b border-[var(--ink)] py-2 text-base text-[var(--ink)] focus:outline-none focus:border-[var(--accent)] transition-colors"
                            />
                        </div>
                        <div>
                            <label className="block text-xs uppercase tracking-widest text-[var(--ink-muted)] mb-2">
                                {t("auth.country")}
                            </label>
                            <select
                                data-testid="register-country-select"
                                value={country}
                                onChange={(e) => setCountry(e.target.value)}
                                className="w-full bg-transparent border-b border-[var(--ink)] py-2 text-base text-[var(--ink)] focus:outline-none focus:border-[var(--accent)] transition-colors"
                            >
                                <option value="ES">España</option>
                                <option value="MX">México</option>
                                <option value="AR">Argentina</option>
                                <option value="CO">Colombia</option>
                                <option value="UK">United Kingdom</option>
                                <option value="US">United States</option>
                                <option value="FR">France</option>
                                <option value="DE">Germany</option>
                                <option value="IT">Italy</option>
                                <option value="PT">Portugal</option>
                            </select>
                        </div>
                    </div>
                    <p className="text-xs text-[var(--ink-dim)] -mt-2">
                        {t("auth.postal_code_why")}
                    </p>

                    <label className="flex items-start gap-3 cursor-pointer pt-2">
                        <input
                            type="checkbox"
                            data-testid="register-email-optin"
                            checked={emailOptin}
                            onChange={(e) => setEmailOptin(e.target.checked)}
                            className="mt-1 h-4 w-4 accent-[var(--accent)] cursor-pointer"
                        />
                        <span className="text-xs text-[var(--ink-muted)] leading-relaxed">
                            {t("auth.monthly_email_optin")}
                        </span>
                    </label>

                    <button
                        data-testid="register-submit-btn"
                        type="submit"
                        disabled={loading}
                        className="w-full bg-[var(--ink)] text-[var(--bg)] rounded-full hover:bg-[var(--accent)] disabled:opacity-50 transition-colors px-8 py-3.5 text-sm uppercase tracking-widest mt-4"
                    >
                        {loading ? t("auth.submit_register_loading") : t("auth.submit_register")}
                    </button>
                </form>
                <p className="text-center text-sm text-[var(--ink-muted)] mt-8">
                    {t("auth.have_account")}{" "}
                    <Link
                        to="/acceder"
                        data-testid="go-to-login-link"
                        className="text-[var(--ink)] underline underline-offset-4 hover:text-[var(--accent)]"
                    >
                        {t("auth.go_login")}
                    </Link>
                </p>
            </section>
        </main>
    );
}
